"use client";

import { useState, useEffect, useCallback } from "react";
import { CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import Image from "next/image";

import { StudioMemberRole } from "@prisma/client";

import {
  Button,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  Input,
  Label,
  Textarea,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "ui";

import { CoachWithMemberProfile, MemberWithProfile } from "@/lib/types";
import { trpc } from "@/lib/trpc/client";
import { getCoachLabel, getCoachLabelColor } from "@/lib/get-coach-labels";

import Uploader from "@/components/form/uploader";

export default function CreateCoachDrawer({
  open,
  onOpenChange,
  handleCreateCoach,
  isLoading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  handleCreateCoach: () => void;
  isLoading: boolean;
}) {
  const [displayName, setDisplayName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [bio, setBio] = useState<string>("");
  const [image, setImage] = useState<string>("");
  const [role, setRole] = useState<StudioMemberRole>(StudioMemberRole.COACH);
  const [isUploadingImage, setIsUploadingImage] = useState<boolean>(false);
  const [selectedAdmin, setSelectedAdmin] = useState<MemberWithProfile | null>(
    null,
  );
  const [activeTab, setActiveTab] = useState<"select" | "invite">("select");
  const [adminMembers, setAdminMembers] = useState<MemberWithProfile[]>([]);
  const [existingCoaches, setExistingCoaches] = useState<
    CoachWithMemberProfile[]
  >([]);

  const utils = trpc.useUtils();
  const createCoachMutation = trpc.coaches.create.useMutation();

  const handleClose = useCallback(() => {
    setDisplayName("");
    setEmail("");
    setBio("");
    setSelectedAdmin(null);
    onOpenChange(false);
  }, [onOpenChange]);

  useEffect(() => {
    if (!open) {
      handleClose();
    }
  }, [handleClose, open]);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      try {
        const [admins, coaches] = await Promise.all([
          utils.client.members.admins.query(),
          utils.coaches.listAll.fetch(),
        ]);
        setAdminMembers((admins as unknown as MemberWithProfile[]) || []);
        setExistingCoaches(
          (coaches as unknown as CoachWithMemberProfile[]) || [],
        );
      } catch (_) {
        // ignore
      }
    };
    load();
  }, [open, utils]);

  const onCreateCoach = async () => {
    try {
      let createdCoach;

      if (activeTab === "select") {
        createdCoach = await createCoachMutation.mutateAsync({
          name: selectedAdmin?.profile?.displayName ?? "",
          bio: selectedAdmin?.profile?.bio ?? "",
          email: selectedAdmin?.user?.email ?? "",
          image: selectedAdmin?.profile?.image ?? "",
          role: selectedAdmin?.role ?? StudioMemberRole.COACH,
        });
      } else {
        createdCoach = await createCoachMutation.mutateAsync({
          name: displayName,
          bio,
          email,
          image,
          role,
        });
      }

      if (!createdCoach.member) {
        throw new Error("Failed to create coach member");
      }

      handleCreateCoach();
      toast.success("Coach created successfully");

      handleClose();
    } catch (error) {
      toast.error("Failed to create coach");
      console.error(error);
    }
  };

  return (
    <Drawer open={open} onOpenChange={handleClose}>
      <DrawerContent className="fixed bottom-0 left-0 right-0 mt-24 flex h-[96%] flex-col rounded-t-[10px] bg-card">
        <div className="mx-auto flex w-full flex-col overflow-auto px-4 pb-8 sm:px-6 md:w-3/4 lg:w-1/2">
          <DrawerHeader>
            <>
              <div className="mt-4 flex flex-col justify-between gap-4 sm:mt-8 sm:flex-row">
                <div className="flex flex-col gap-2">
                  <DrawerTitle className="text-left text-xl font-bold text-gray-900 dark:text-gray-100 sm:text-2xl">
                    Create Coach
                  </DrawerTitle>
                  <DrawerDescription className="text-md text-left leading-6 text-muted-foreground">
                    Coaches are authors of content that members can see and
                    engage with. You can either promote an existing admin to a
                    coach or invite someone new to join as a coach. Coaches have
                    access to the Studio dashboard and can create and manage
                    content.
                  </DrawerDescription>
                </div>
                <DrawerClose asChild>
                  <Button
                    variant="ghost"
                    disabled={isLoading}
                    onClick={handleClose}
                    className="self-start"
                  >
                    <X size={16} />
                  </Button>
                </DrawerClose>
              </div>
            </>
          </DrawerHeader>

          <Tabs
            defaultValue="select"
            className="mt-8 w-full"
            onValueChange={(value) =>
              setActiveTab(value as "select" | "invite")
            }
          >
            <TabsList className="grid w-full grid-cols-2 bg-transparent">
              <TabsTrigger
                value="select"
                className="relative rounded-none border-b border-gray-200 px-4 py-2 text-sm font-medium text-gray-500 before:absolute before:bottom-0 before:left-0 before:h-0.5 before:w-full before:scale-x-0 before:bg-gray-900 before:transition-transform hover:text-gray-700 data-[state=active]:bg-transparent data-[state=active]:text-gray-900 data-[state=active]:shadow-none data-[state=active]:before:scale-x-100 dark:border-border dark:text-muted-foreground dark:before:bg-white dark:hover:text-foreground dark:data-[state=active]:text-foreground"
              >
                Select Current Admin
              </TabsTrigger>
              <TabsTrigger
                value="invite"
                className="relative rounded-none border-b border-gray-200 px-4 py-2 text-sm font-medium text-gray-500 before:absolute before:bottom-0 before:left-0 before:h-0.5 before:w-full before:scale-x-0 before:bg-gray-900 before:transition-transform hover:text-gray-700 data-[state=active]:bg-transparent data-[state=active]:text-gray-900 data-[state=active]:shadow-none data-[state=active]:before:scale-x-100 dark:border-border dark:text-muted-foreground dark:before:bg-white dark:hover:text-foreground dark:data-[state=active]:text-foreground"
              >
                Invite Coach
              </TabsTrigger>
            </TabsList>

            <TabsContent value="select">
              <div className="mt-6 flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  {adminMembers
                    .filter((member) => !member.isGhostAdmin)
                    .map((member) => (
                      <div
                        key={member.id}
                        className={`flex items-center justify-between rounded-lg border border-gray-200 p-4 dark:border-border ${
                          existingCoaches.some(
                            (coach) => coach.member?.id === member.id,
                          )
                            ? "cursor-not-allowed opacity-50"
                            : "cursor-pointer hover:bg-gray-50 dark:hover:bg-secondary"
                        }`}
                        onClick={() => {
                          if (
                            !existingCoaches.some(
                              (coach) => coach.member?.id === member.id,
                            )
                          ) {
                            setSelectedAdmin(member);
                          }
                        }}
                      >
                        <div className="flex items-center gap-4">
                          <div className="relative flex h-10 w-10 flex-col items-center justify-end overflow-hidden rounded-md">
                            <Image
                              src={
                                member.profile?.image ??
                                `https://avatar.vercel.sh/${member.profile?.displayName}`
                              }
                              fill
                              alt={member.profile?.displayName ?? "User avatar"}
                              className="h-full w-full rounded-md object-cover"
                            />
                          </div>
                          <div className="flex flex-col items-start justify-start gap-1">
                            <p className="font-medium text-gray-900 dark:text-gray-100">
                              {member.profile?.displayName}
                            </p>
                            <span
                              className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs ${getCoachLabelColor(member.role as StudioMemberRole)}`}
                            >
                              {getCoachLabel(member.role)}
                            </span>
                          </div>
                        </div>
                        {selectedAdmin?.id === member.id ? (
                          <CheckCircle2 className="h-6 w-6 text-green-500" />
                        ) : (
                          <div className="h-6 w-6 rounded-full border border-gray-300 dark:border-border" />
                        )}
                      </div>
                    ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="invite">
              <div className="mt-6 flex flex-col gap-2 p-2">
                <div className="mx-auto my-4 h-[240px] w-[240px] rounded-lg shadow-lg">
                  <Uploader
                    defaultValue={image}
                    name="logo"
                    loading={isUploadingImage}
                    onChange={async (_: string, file?: File) => {
                      if (!file) return;
                      setIsUploadingImage(true);
                      await fetch("/api/upload", {
                        method: "POST",
                        headers: {
                          "content-type":
                            file.type || "application/octet-stream",
                        },
                        body: file,
                      }).then(async (res) => {
                        if (res.status === 200) {
                          const { url } = await res.json();

                          setImage(url);
                          setIsUploadingImage(false);

                          toast.success("Image uploaded successfully");
                        } else {
                          const error = await res.text();
                          toast.error(error);

                          setIsUploadingImage(false);
                        }
                      });
                    }}
                  />
                </div>
                <div className="mt-4 flex flex-col gap-6 sm:gap-8">
                  <div className="flex flex-col gap-2">
                    <Label className="text-gray-900 dark:text-gray-100">
                      Display Name
                    </Label>
                    <Input
                      type="text"
                      placeholder="Display Name"
                      value={displayName}
                      onChange={(e) =>
                        setDisplayName(e.target.value.slice(0, 64))
                      }
                      maxLength={64}
                      disabled={isLoading}
                      className="bg-card text-gray-900 dark:text-gray-100"
                    />
                    <p className="text-left text-sm text-muted-foreground">
                      {displayName.length}/64
                    </p>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label className="text-gray-900 dark:text-gray-100">
                      Email
                    </Label>
                    <Input
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                      className="bg-card text-gray-900 dark:text-gray-100"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label className="text-gray-900 dark:text-gray-100">
                      Bio
                    </Label>
                    <Textarea
                      placeholder="Coach Bio"
                      value={bio}
                      onChange={(e) => setBio(e.target.value.slice(0, 500))}
                      maxLength={500}
                      disabled={isLoading}
                      className="bg-card text-gray-900 dark:text-gray-100"
                    />
                    <p className="text-left text-sm text-muted-foreground">
                      {bio.length}/500
                    </p>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label className="text-gray-900 dark:text-gray-100">
                      Role
                    </Label>
                    <select
                      value={role}
                      onChange={(e) => {
                        setRole(e.target.value as StudioMemberRole);
                      }}
                      className="w-full rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground focus:outline-none focus:ring-black dark:focus:ring-white"
                    >
                      <option value={StudioMemberRole.COACH}>Coach</option>
                      <option value={StudioMemberRole.GUEST_COACH}>
                        Guest Coach
                      </option>
                      <option value={StudioMemberRole.ADMIN}>Admin</option>
                    </select>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <div className="mt-8 flex flex-col justify-end gap-4 sm:flex-row">
            <DrawerClose asChild>
              <Button
                className="w-full p-4 sm:w-auto"
                variant="outline"
                disabled={isLoading}
                onClick={handleClose}
              >
                Cancel
              </Button>
            </DrawerClose>
            <Button
              className="w-full p-4 sm:w-auto"
              variant="default"
              onClick={onCreateCoach}
              disabled={
                isLoading ||
                (activeTab === "select" && !selectedAdmin) ||
                (activeTab === "invite" && (!displayName || !email))
              }
            >
              {isLoading ? "Creating..." : "Create Coach"}
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
